import { Navigate, Route, useLocation } from 'react-router-dom'
import store, { RootState } from 'state/store'
import React, { FC, ReactElement, ReactNode } from 'react'
import { UiState } from 'state/ui/index'
import { Page404Container } from 'components/containers'
import useRbac from 'utils/custom-hooks/useRbac'

interface RouteProtectorProps {
  children?: ReactNode
  path?: string
  element?: ReactElement
  isOrgModeRoute?: boolean
  featureFlag?: boolean
  isSettingsRoute?: boolean
}

/**
 * This is the wrapper for routes that should not be available in org mode.
 */
const RouteProtector: FC<RouteProtectorProps> = ({
  children,
  path = '',
  element = undefined,
  isOrgModeRoute = false,
  featureFlag = false,
  isSettingsRoute = false,
}) => {
  // Can't use `useSelector(state => state.ui)` here because it will cause an infinite rerender
  const { ui = {} as UiState }: RootState = store.getState()
  const { isRouteAuthorized } = useRbac()

  const { inOrgMode } = ui
  const location = useLocation()
  const normalizedLocation = location.pathname.split('/app/')[1]
  const routeIsAuthorized = isRouteAuthorized(normalizedLocation)

  if (!featureFlag || !routeIsAuthorized) {
    return <Page404Container homeRoute="/app/purchases" routesAreReady />
  }

  // This checks if we're in org mode, and checks the route is appropriate if we are
  // Also switches the redirect depending on if we're in settings or the general app
  if (inOrgMode && !isOrgModeRoute) {
    return isSettingsRoute ? (
      <Navigate to={'/app/settings/dispatch/drivers'} replace />
    ) : (
      <Navigate to={'/app/load-board/active-tickets'} replace />
    )
  }

  return (
    <Route path={path} element={element}>
      {children}
    </Route>
  )
}

export default RouteProtector
